import { Button, Col, Drawer, Form, Input, Row, Select, Tabs, TreeSelect } from 'antd';
import { Formik } from 'formik';
import { FiCheck, FiPlus } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import type { RawRegulationsDrawerProps } from '../../RawRegulations.types.ts';
import {
  RAW_REGULATIONS_FORM,
  RAW_REGULATIONS_FORM_SCHEMA,
  RAW_REGULATIONS_INITIAL_FORM_VALUES,
} from '../../RawRegulations.constants.tsx';

import { useTranslation } from 'react-i18next';
import { DebounceSelect } from '../../../../components/debonce-select';
import { searchLeadership } from '../../../organization-unit-lists/OrganizationUnitLists.services.ts';
import type { TopicType } from '../../../topics/Topics.types.ts';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../constants/app.ts';

import './RawRegulations.styles.css';
import { getRawRegulationValues, normalize } from '../../RawRegulations.helpers.ts';
import { useService } from '../../RawRegulations.hooks.ts';
import { useService as useTopicsService } from '../../../topics/Topics.hooks.ts';
import { useService as useUnitsService } from '../../../organization-unit-lists/OrganizationUnitLists.hooks.ts';
import { buildTree, buildTreeByAbbreviation, getParentTopic } from '../../../../helpers';
import { type Key, useState } from 'react';
import { TreeModal } from '../../../../components/tree-model';
import { Tag } from '../../../draft-regulation/components/tag';

export default function RawRegulationsDrawer({
  onClose,
  open,
  onSubmit,
  regulationId,
}: RawRegulationsDrawerProps) {
  const { t } = useTranslation();

  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState<Key[]>([]);

  const { regulation } = useService({ regulationId });
  const { topics } = useTopicsService({ topicId: null });
  const { units } = useUnitsService({ organizationUnitListId: null });

  const unitsTree = units
    ? buildTreeByAbbreviation(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        units.map((unit: { id: number }) => ({
          ...unit,
          key: unit.id.toString(),
        })),
      )
    : [];

  const topicsTree = buildTree(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    topics?.map((topic: TopicType) => ({
      id: topic.id,
      key: topic.id,
      parentId: topic.parentTopicId,
      title: topic.name,
      name: topic.name,
    })),
  );

  const data = normalize(regulation);

  const initialValues = regulationId
    ? getRawRegulationValues(data)
    : RAW_REGULATIONS_INITIAL_FORM_VALUES;

  const handleOpenTopicsModal = () => setShowTopicsModal(true);
  const handleCloseTopicsModal = () => setShowTopicsModal(false);

  return (
    <Drawer
      title={data?.title || ''}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={RAW_REGULATIONS_FORM}
            >
              {t('common.confirm')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={RAW_REGULATIONS_FORM_SCHEMA}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          const handleDeleteTopic = (uid: number | string) => {
            setFieldValue(
              'topics',
              values.topics.filter((u: number | string) => u !== uid),
            );
          };

          const Properties = () => (
            <>
              <Form.Item
                label={t('form.field.title')}
                validateStatus={touched.title && errors.title ? 'error' : ''}
                help={touched.title && typeof errors.title === 'string' ? errors.title : undefined}
              >
                <Input
                  name="title"
                  variant="underlined"
                  value={values.title}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>
              <Form.Item
                label={t('form.field.title-english')}
                validateStatus={touched.titleEnglish && errors.titleEnglish ? 'error' : ''}
                help={
                  touched.titleEnglish && typeof errors.titleEnglish === 'string'
                    ? errors.titleEnglish
                    : undefined
                }
              >
                <Input
                  name="titleEnglish"
                  variant="underlined"
                  value={values.titleEnglish}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>
              <Form.Item
                label={t('form.field.author')}
                validateStatus={touched.author && errors.author ? 'error' : ''}
                help={
                  touched.author && typeof errors.author === 'string' ? errors.author : undefined
                }
              >
                <DebounceSelect
                  mode="multiple"
                  value={values.author}
                  placeholder={t('form.placeholder.type-here')}
                  fetchOptions={(term) => searchLeadership(term)}
                  style={{ width: '100%' }}
                  onChange={(newValue) => {
                    if (Array.isArray(newValue)) {
                      setFieldValue('author', newValue);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.confirmation-period')}
                validateStatus={
                  touched.confirmationPeriod && errors.confirmationPeriod ? 'error' : ''
                }
                help={
                  touched.confirmationPeriod && typeof errors.confirmationPeriod === 'string'
                    ? errors.confirmationPeriod
                    : undefined
                }
              >
                <Select
                  variant="outlined"
                  value={values.confirmationPeriod}
                  onChange={(e) => setFieldValue('confirmationPeriod', e)}
                  placeholder={t('form.placeholder.select-here')}
                  options={
                    values.type === 2
                      ? [
                          { value: 2, label: t('form.option.six-months') },
                          { value: 1, label: t('form.option.twelve-months') },
                          { value: 3, label: t('form.option.thirty-six-months') },
                        ]
                      : [
                          { value: 2, label: t('form.option.six-months') },
                          { value: 1, label: t('form.option.twelve-months') },
                        ]
                  }
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.emerged-out')}
                validateStatus={touched.emergedOut && errors.emergedOut ? 'error' : ''}
                help={
                  touched.emergedOut && typeof errors.emergedOut === 'string'
                    ? errors.emergedOut
                    : undefined
                }
              >
                <Input
                  width="100%"
                  name="emergedOut"
                  variant="outlined"
                  value={values.emergedOut}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>
              <Form.Item
                label={t('form.field.unit-in-charge')}
                validateStatus={touched.unitInChargeId && errors.unitInChargeId ? 'error' : ''}
                help={
                  touched.unitInChargeId && typeof errors.unitInChargeId === 'string'
                    ? errors.unitInChargeId
                    : undefined
                }
              >
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="title"
                  variant="outlined"
                  placeholder={t('form.placeholder.select-here')}
                  value={values.unitInChargeId}
                  treeCheckable
                  allowClear
                  onSelect={(e) => {
                    setFieldValue('unitInChargeId', e);
                  }}
                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                  treeData={unitsTree}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.topic')}
                validateStatus={touched.topics && errors.topics ? 'error' : ''}
                help={
                  touched.topics && typeof errors.topics === 'string' ? errors.topics : undefined
                }
              >
                <div className="tags-container-column">
                  {values?.topics?.map((topic: string) => {
                    const node = topics.find((tc: { id: string }) => tc.id === topic);
                    const nodes = getParentTopic(node, [], topics);
                    const parentsNames = nodes
                      .reverse()
                      .map((n: { name: string }) => n.name)
                      .join('  >  ');
                    const title = `${parentsNames}  >  ${node?.name}`;
                    return (
                      <div>
                        <Tag id={topic} title={title} onDelete={handleDeleteTopic} />
                      </div>
                    );
                  })}
                </div>
                <div onClick={handleOpenTopicsModal} className="add-link">
                  {t('common.add')} <FiPlus />
                </div>
              </Form.Item>

              {/*<Form.Item*/}
              {/*  label={t('form.field.audience')}*/}
              {/*  validateStatus={touched.targetGroupId && errors.targetGroupId ? 'error' : ''}*/}
              {/*  help={*/}
              {/*    touched.targetGroupId && typeof errors.targetGroupId === 'string'*/}
              {/*      ? errors.targetGroupId*/}
              {/*      : undefined*/}
              {/*  }*/}
              {/*>*/}
              {/*  <Select*/}
              {/*    variant="outlined"*/}
              {/*    placeholder={t('form.placeholder.select-here')}*/}
              {/*    value={values.targetGroupId}*/}
              {/*    onChange={(e) => setFieldValue('targetGroupId', e)}*/}
              {/*    options={groups?.map((group: TopicType) => ({*/}
              {/*      value: group.id,*/}
              {/*      label: group.name,*/}
              {/*    }))}*/}
              {/*  />*/}
              {/*</Form.Item>*/}
            </>
          );
          const Extra = () => (
            <>
              <div className="extra-info">
                <div className="extra-info-row">
                  <span className="extra-info-title">{t('pages.raw-regulations.publication')}</span>
                  <span>
                    {data?.publicationDate ? dayjs(data?.publicationDate).format(DATE_FORMAT) : '-'}
                  </span>
                </div>
                <div className="extra-info-row">
                  <span className="extra-info-title">{t('pages.raw-regulations.deletion')}</span>
                  <span>
                    {data?.deletionDate ? dayjs(data?.deletionDate).format(DATE_FORMAT) : '-'}
                  </span>
                </div>
                <div className="extra-info-row">
                  <span className="extra-info-title">{t('pages.raw-regulations.expiration')}</span>
                  <span>
                    {data?.confirmationDate
                      ? dayjs(data?.confirmationDate).format(DATE_FORMAT)
                      : '-'}
                  </span>
                </div>
                <div className="extra-info-row">
                  <span className="extra-info-title">{t('pages.raw-regulations.attachments')}</span>
                  <span>{data?.attachments?.length || '-'}</span>
                </div>
                <div className="extra-info-column">
                  <span className="extra-info-title">{t('form.field.short-description')}</span>
                  <span dangerouslySetInnerHTML={{ __html: data?.shortDescription || '' }} />
                </div>
                <div className="extra-info-column">
                  <span className="extra-info-title">
                    {t('form.field.short-description-english')}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: data?.shortDescriptionEnglish || '' }} />
                </div>
                <div className="extra-info-column">
                  <span className="extra-info-title">{t('form.field.body-template')}</span>
                  <span dangerouslySetInnerHTML={{ __html: data?.content || '' }} />
                </div>
              </div>
            </>
          );
          const tabs = [
            {
              key: '1',
              label: t('tab.properties'),
              children: <Properties />,
            },
            {
              key: '2',
              label: t('tab.extra'),
              children: <Extra />,
            },
          ];
          return (
            <Form layout="vertical" onFinish={handleSubmit} id={RAW_REGULATIONS_FORM}>
              <Form.Item
                label={t('form.field.is-active')}
                validateStatus={touched.isActive && errors.isActive ? 'error' : ''}
                help={
                  touched.isActive && typeof errors.isActive === 'string'
                    ? errors.isActive
                    : undefined
                }
              >
                <Select
                  variant="underlined"
                  value={values.isActive}
                  placeholder={t('form.placeholder.select-here')}
                  onChange={(e) => setFieldValue('isActive', e)}
                  disabled={Boolean(!data?.isDeleted)}
                  options={[
                    { value: true, label: t('common.yes') },
                    { value: false, label: t('common.no') },
                  ]}
                />
              </Form.Item>

              <div className="regulation-number">
                <div className="label">{t('form.field.number')}</div>
                <div className="number">{data?.regulationNumber}</div>
              </div>

              <Tabs defaultActiveKey="1" style={{ color: '#98cb00' }} items={tabs} />
              {showTopicsModal && (
                <TreeModal
                  title={t('form.field.topic')}
                  open={showTopicsModal}
                  selected={values.topics}
                  expandedKeys={expandedTopics}
                  onExpand={setExpandedTopics}
                  treeData={topicsTree}
                  onClose={handleCloseTopicsModal}
                  onSubmit={(val) => setFieldValue('topics', val)}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
